import { usePostWorkspaceEntityTypeEntityGenerateOverview } from '@api/endpoints/workspaces/entity-types/post-workspace-entity-type-entity-generate-overview'
import { Skeleton } from '@design/components/loaders/skeleton/Skeleton'
import { MarkdownBody } from '@design/components/markdown/MarkdownBody'
import { ErrorMessage } from '@design/components/messages/error-message/ErrorMessage'
import { useTranslation } from 'react-i18next'

interface EntityOverviewTabProps {
  workspaceId: string
  propertyDfnId: string
  standardId: string
}

export function EntityOverviewTab({
  workspaceId,
  propertyDfnId,
  standardId,
}: EntityOverviewTabProps) {
  const { t } = useTranslation()
  const {
    data: entityOverview,
    isLoading,
    isError,
  } = usePostWorkspaceEntityTypeEntityGenerateOverview({
    params: {
      workspace_id: workspaceId,
      property_dfn_id: propertyDfnId,
      standard_id: standardId,
    },
    payload: {},
  })

  if (isError) {
    return <ErrorMessage>{t('entities.overview.loadError')}</ErrorMessage>
  }

  if (isLoading || !entityOverview) return <OverviewSkeleton />

  return (
    <div className='space-y-6'>
      <Section
        title={t('entities.overview.summary')}
        content={entityOverview.summary}
      />
      <Section
        title={t('entities.overview.keyObservations')}
        content={entityOverview.keyObservations}
      />
      <Section
        title={t('entities.overview.riskFactors')}
        content={entityOverview.riskFactors}
      />
      <Section
        title={t('entities.overview.recommandations')}
        content={entityOverview.recommandations}
      />
    </div>
  )
}

function Section({ title, content }: { title: string; content: string }) {
  return (
    <div className='space-y-2'>
      <h3 className='font-semibold text-foreground text-sm'>{title}</h3>
      <MarkdownBody markdown={content} />
    </div>
  )
}

function OverviewSkeleton() {
  return (
    <div className='space-y-6'>
      {[0, 1, 2, 3].map((i) => (
        <div key={i}>
          <Skeleton className='h-4 w-1/3' />
          <div className='mbs-2 space-y-1.5'>
            <Skeleton className='h-3.5 w-full' />
            <Skeleton className='h-3.5 w-5/6' />
            <Skeleton className='h-3.5 w-3/4' />
          </div>
        </div>
      ))}
    </div>
  )
}
